import React, { MouseEventHandler, useEffect, useState } from "react";
import { Organization, MapMarker } from "../common/ObjectTypes";
import { AddIcon, DeleteIcon, PawIcon } from "../common/ProjectIcons";
import TextInput from "../common/TextInput";
import { Link } from "react-router-dom";
import SelectInput from "../common/SelectInput";
import VideoManagementPage from "../video/VideoManagementPage";
import { getIconImage } from "../common/ProjectIcons";
import { get } from "http";
import MultiStepForm from "../common/MultiStepForm";
import { MoonLoader } from "react-spinners";
import { MapMarkerIcons } from "../common/ObjectTypes";
import { getCurrentUser } from "../../helpers/authHeader";
import { getWildXROrgId } from "../../helpers/wildXROrgIdHelper";

type AddEditMapMarkerTypes = {
  mapMarker: MapMarker;
  organizations: Organization[];
  addMapMarker: boolean;
  onClickAdd: Function;
  onSelectMapMarker: Function;
  onChange: Function;
  onCancelEdit: Function;
  onCancelAdd: Function;
  onUpdateMapMarker: Function;
  onDelete: Function;
  existingMapMarker: boolean;
  selectVideosPage?: boolean;
  onChangeSingleSelect: Function;
  onSelectVideoForMapMarker: Function;
  onSave: MouseEventHandler;
  changesMade: boolean;
  saving: boolean;
  errors: any;
};

const AddEditMapMarker = ({
  mapMarker,
  organizations,
  onChange,
  onSave,
  selectVideosPage,
  onChangeSingleSelect,
  onSelectVideoForMapMarker,
  existingMapMarker,
  changesMade,
  saving,
  errors,
}: AddEditMapMarkerTypes) => {
  const [readOnly, setReadOnly] = useState(false);
  const wildXROrgId = getWildXROrgId();
  const currentUser = getCurrentUser();
  const allVideos = [...(mapMarker.videos || [])];
  const isMapMarkerVideoIncluded = (videoId: string): boolean => {
    return allVideos.some((video) => video.videoId === videoId);
  };

  const getOrganizationById = (id: any) => {
    return organizations.find((org) => org.organizationId === id);
  };
  const defaultOrg = getOrganizationById(mapMarker.organizationId);

  const getIconNameById = (id: any) => {
    return MapMarkerIcons.find((icon) => icon.value === id);
  };
  const defaultIcon = getIconNameById(mapMarker.iconID);

  const cannotAccessMultiStep =
    mapMarker.organizationId === wildXROrgId &&
    !currentUser.isSystemAdmin &&
    !currentUser.organizations?.some(
      (org: any) => org.organizationId === wildXROrgId
    );

  //multistep form handling
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { label: "Edit Map Marker" },
    { label: "Videos" },
    { label: "Review & Save" },
  ];

  const isSystemAdmin = currentUser?.isSystemAdmin;
  const hasOrganizationId = mapMarker?.organizationId;
  const isDisabled =
    readOnly || (existingMapMarker && !isSystemAdmin && !hasOrganizationId);

  const handleNext = () => {
    setCurrentStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
  };
  useEffect(() => {
    if (cannotAccessMultiStep) {
      setCurrentStep(steps.length - 1);
      setReadOnly(true);
    } else {
      setReadOnly(currentStep === 2);
    }
  }, [currentStep, currentUser]);

  const handleBack = () => {
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  return (
    <div className="w-full flex flex-col justify-center relative">
      <h1 className="text-3xl text-center my-4 font-bold">
        {existingMapMarker ? "Edit Map Marker" : "Add Map Marker"}
      </h1>
      <div className={cannotAccessMultiStep ? "hidden" : "visible" + " mb-2"}>
        <MultiStepForm
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      </div>
      <>
        {errors.onSave && (
          <div className="flex justify-center w-full mb-2">
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative w-1/2 mx-auto "
              role="alert"
            >
              <span className="block sm:inline">{errors.onSave}</span>
            </div>
          </div>
        )}
        {(currentStep === 0 || currentStep === 2) && (
          <div className="w-2/3 my-2 bg-grayScale-500 bg-opacity-50 mx-auto">
            <div className="h-2/3 flex justify-center">
              <div className="w-full">
                <div className=" p-2 h-3/5 my-6 ml-3 mr-6">
                  <div className="drop-shadow-lg mb-6">
                    <div
                      className={
                        "bg-newGray-500 px-2 flex flex-col justify-between"
                      }
                    >
                      <TextInput
                        name={"name"}
                        label={"Name*"}
                        placeholder={"Enter Name"}
                        value={mapMarker.name ?? ""}
                        error={errors.name}
                        disabled={isDisabled}
                        className={"bg-white-800 p-2 w-full rounded shadow"}
                        type={"text"}
                        initialFocus={true}
                        onChange={(e: any) => onChange(e)}
                      />
                      <TextInput
                        name={"description"}
                        label={"Description"}
                        placeholder={""}
                        value={mapMarker.description ?? ""}
                        error={errors.description}
                        disabled={isDisabled}
                        className={"bg-white-800 p-2 w-full rounded shadow"}
                        type={"text"}
                        initialFocus={true}
                        onChange={(e: any) => onChange(e)}
                      />
                      {currentUser.isSystemAdmin ||
                      currentUser.organizations.length > 1 ? (
                        <div className="flex flex-col mb-2 ">
                          <SelectInput
                            key={
                              defaultOrg ? defaultOrg.organizationId : "default"
                            }
                            name="organizationId"
                            label="Select Organization *Select an organization to create a custom Map Marker*"
                            options={organizations.map((org: any) => ({
                              value: org.organizationId,
                              label: org.name,
                            }))}
                            onChange={(e: any) =>
                              onChangeSingleSelect("organizationId", e)
                            }
                            className="w-full"
                            defaultValue={
                              mapMarker !== null && defaultOrg
                                ? {
                                    value: defaultOrg.organizationId,
                                    label: defaultOrg.name,
                                  }
                                : undefined
                            }
                            isDisabled={isDisabled}
                            isLoading={false}
                            isClearable={true}
                            isSearchable={true}
                            isMulti={false}
                            errors={errors.organizationId}
                          />
                        </div>
                      ) : (
                        <>
                          {currentUser.organizations.map((org: any) => {
                            return (
                              <div className="py-2" key={org.organizationId}>
                                <span>Organization: </span>
                                {mapMarker.organizationId === wildXROrgId
                                  ? "WildXR"
                                  : org.name}
                              </div>
                            );
                          })}
                        </>
                      )}
                      {(mapMarker.organizationId ||
                        currentUser.organizations.length > 0) && (
                        <SelectInput
                          name="iconID"
                          label="Icon"
                          options={MapMarkerIcons.map((icon) => ({
                            value: icon.value,
                            label: icon.label,
                          }))}
                          defaultValue={
                            mapMarker !== null && defaultIcon
                              ? {
                                  value: defaultIcon.value,
                                  label: defaultIcon.label,
                                }
                              : undefined
                          }
                          className="bg-white-800 w-full rounded shadow"
                          isDisabled={isDisabled}
                          isLoading={false}
                          isClearable={true}
                          isSearchable={true}
                          isMulti={false}
                          errors={() => {}}
                          onChange={(e: any) => {
                            onChangeSingleSelect("iconID", e);
                          }}
                        />
                      )}
                      <TextInput
                        name={"latitude"}
                        label={"Latitude"}
                        placeholder={""}
                        value={mapMarker.latitude.toString() ?? ""}
                        error={errors.latitude}
                        disabled={isDisabled}
                        className={"bg-white-800 p-2 w-full rounded shadow"}
                        type={"text"}
                        initialFocus={true}
                        onChange={(e: any) => onChange(e)}
                      />
                      <TextInput
                        name={"longitude"}
                        label={"Longitude"}
                        placeholder={""}
                        value={mapMarker.longitude.toString() ?? ""}
                        error={errors.longitude}
                        disabled={isDisabled}
                        className={"bg-white-800 p-2 w-full rounded shadow"}
                        type={"text"}
                        initialFocus={true}
                        onChange={(e: any) => onChange(e)}
                      />
                    </div>
                    <div className="w-full flex flex-col py-2">
                      <div className=" px-2">
                        {/* Species */}

                        {readOnly && (
                          <>
                            <h2 className="flex font-bold text-xl mb-1">
                              Videos
                            </h2>
                            <div className="bg-grayScale-200 p-2 my-2">
                              {mapMarker.videos?.length}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {currentStep === 1 && (
          <VideoManagementPage
            selectVideosPage={true}
            onSelectVideoForMapMarker={onSelectVideoForMapMarker}
            isMapMarkerVideoIncluded={isMapMarkerVideoIncluded}
            isDisabled={isDisabled}
          />
        )}
        <div className={cannotAccessMultiStep ? "hidden" : "visible"}>
          <div className="flex justify-center items-center">
            <div className="px-6 h-10 mt-4 flex justify-between w-2/3">
              <button
                className="px-4 py-2 bg-deepGreen-500 text-white rounded"
                onClick={handleBack}
                disabled={currentStep === 0}
              >
                Back
              </button>
              {currentStep < steps.length - 1 ? (
                <button
                  className="px-4 py-2 bg-deepGreen-500 text-white rounded"
                  onClick={handleNext}
                >
                  Next
                </button>
              ) : (
                <>
                  {saving ? (
                    <div className="flex justify-center items-center">
                      <MoonLoader color="#9EB45B" />
                    </div>
                  ) : (
                    <button
                      onClick={onSave}
                      className={
                        !changesMade
                          ? "px-4 py-2 bg-green-200 text-gray-600 rounded"
                          : "px-4 py-2 bg-deepGreen-500 text-white rounded"
                      }
                      disabled={!changesMade}
                    >
                      Save
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default AddEditMapMarker;
